import type { ApolloCache } from '@apollo/client';
import type {
  GetAdminRegistryRegistriesQuery,
  GetFinalizedDateAndGiftCountQuery,
  GetRegistryChecklistQuery,
  GetRegistryChecklistSectionByIdQuery,
  GetRegistryRecommendationResultsQuery,
  RegistryGiftBasketCountByEventNameQuery,
} from '@graphql/generated';
import {
  GetAdminRegistryRegistriesDocument,
  GetFinalizedDateAndGiftCountDocument,
  GetRegistryChecklistDocument,
  GetRegistryChecklistSectionByIdDocument,
  GetRegistryRecommendationResultsDocument,
  RegistryGiftBasketCountByEventNameDocument,
} from '@graphql/generated';

interface UpdateRegistriesAfterItemDeletergs {
  eventHandle: Maybe<string>;
  cache: ApolloCache<{}>;
  productId: string;
}

export const updateRegistriesAfterItemDelete = ({ eventHandle, cache, productId }: UpdateRegistriesAfterItemDeletergs) => {
  if (eventHandle) {
    const registryRegistriesQueryBaseProps = {
      query: GetAdminRegistryRegistriesDocument,
      variables: { eventHandle },
    };

    const registryRegistriesCacheItem = cache.readQuery<GetAdminRegistryRegistriesQuery>({
      ...registryRegistriesQueryBaseProps,
    });

    if (registryRegistriesCacheItem?.eventByName?.info.registry.length) {
      cache.writeQuery({
        ...registryRegistriesQueryBaseProps,
        data: {
          ...registryRegistriesCacheItem,
          eventByName: {
            ...registryRegistriesCacheItem.eventByName,
            info: {
              ...registryRegistriesCacheItem.eventByName.info,
              registry: registryRegistriesCacheItem.eventByName.info.registry.map(registry => {
                if (registry.items?.length) {
                  return {
                    ...registry,
                    items: registry.items?.filter(item => item.id !== productId),
                  };
                } else {
                  return registry;
                }
              }),
            },
          },
        },
      });
    }
  }
};

interface UpdateGiftBasketAfterItemDeletergs {
  eventHandle: Maybe<string>;
  cache: ApolloCache<{}>;
  productId: string;
}

export const updateGiftBasketAfterItemDelete = ({ eventHandle, cache, productId }: UpdateGiftBasketAfterItemDeletergs) => {
  if (eventHandle) {
    const giftBasketQueryBaseProps = {
      query: RegistryGiftBasketCountByEventNameDocument,
      variables: { handle: eventHandle },
    };

    const giftBasketCacheItem = cache.readQuery<RegistryGiftBasketCountByEventNameQuery>({
      ...giftBasketQueryBaseProps,
    });

    if (giftBasketCacheItem?.eventByName?.registry.items.length) {
      cache.writeQuery({
        ...giftBasketQueryBaseProps,
        data: {
          ...giftBasketCacheItem,
          eventByName: {
            ...giftBasketCacheItem.eventByName,
            registry: {
              ...giftBasketCacheItem.eventByName.registry,
              items: giftBasketCacheItem.eventByName.registry.items.filter(item => item.id !== productId),
            },
          },
        },
      });
    }
  }
};
interface UpdateGiftCountsAfterItemAddArgs {
  eventHandle: Maybe<string>;
  cache: ApolloCache<{}>;
  qty: number;
  valueInMinorUnits?: number;
  isDelete: boolean;
}

export const updateGiftCountsAfterItemAddOrDelete = ({ eventHandle, cache, qty, valueInMinorUnits, isDelete }: UpdateGiftCountsAfterItemAddArgs) => {
  if (eventHandle) {
    const registryAdvisorQueryBaseProps = {
      query: GetRegistryRecommendationResultsDocument,
      variables: { handle: eventHandle },
    };

    const registryAdvisorCacheItem = cache.readQuery<GetRegistryRecommendationResultsQuery>({
      ...registryAdvisorQueryBaseProps,
    });

    const registryAdvisor = registryAdvisorCacheItem?.eventByName?.recommendation.registryAdvisor;

    if (registryAdvisorCacheItem || registryAdvisor) {
      const priceRanges = registryAdvisorCacheItem.eventByName?.recommendation.registryAdvisor?.priceRanges;
      const currentGiftCount = registryAdvisorCacheItem?.eventByName?.recommendation?.registryAdvisor?.currentGiftCount || 0;

      cache.writeQuery({
        ...registryAdvisorQueryBaseProps,
        data: {
          ...registryAdvisorCacheItem,
          eventByName: {
            ...registryAdvisorCacheItem?.eventByName,
            recommendation: {
              ...registryAdvisorCacheItem?.eventByName?.recommendation,
              registryAdvisor: {
                ...registryAdvisorCacheItem?.eventByName?.recommendation?.registryAdvisor,
                currentGiftCount: isDelete ? currentGiftCount - qty : currentGiftCount + qty,
                priceRanges: priceRanges?.map(range => {
                  const endPrice = range.endPrice?.valueInMinorUnits ? range.endPrice?.valueInMinorUnits - 1 : Infinity;
                  const startPrice = range.startPrice?.valueInMinorUnits;
                  if (valueInMinorUnits && endPrice && valueInMinorUnits >= startPrice && valueInMinorUnits <= endPrice) {
                    return {
                      ...range,
                      currentGiftCount: isDelete && range.currentGiftCount >= 1 ? range.currentGiftCount - qty : range.currentGiftCount + qty,
                    };
                  } else {
                    return range;
                  }
                }),
              },
            },
          },
        },
      });
    }

    const giftCountQueryBaseProps = {
      query: GetFinalizedDateAndGiftCountDocument,
      variables: { eventHandle },
    };

    const giftCountCacheItem = cache.readQuery<GetFinalizedDateAndGiftCountQuery>({
      ...giftCountQueryBaseProps,
    });

    if (giftCountCacheItem?.eventByName?.registry?.itemCount) {
      const { everything } = giftCountCacheItem.eventByName.registry.itemCount;

      cache.writeQuery({
        ...giftCountQueryBaseProps,
        data: {
          ...giftCountCacheItem,
          eventByName: {
            ...giftCountCacheItem.eventByName,
            registry: {
              ...giftCountCacheItem.eventByName.registry,
              itemCount: {
                ...giftCountCacheItem.eventByName.registry.itemCount,
                everything: isDelete && everything >= 1 ? everything - qty : everything + qty,
              },
            },
          },
        },
      });
    }
  }
};

export const evictChecklistProgressAndSectionsAfterItemAddOrDelete = ({ cache, eventId }: { cache: ApolloCache<{}>; eventId: string }) => {
  // We need to remove this checklist items by hand from the cache.
  // This way, the Checklist progress and sections will only be refetched if:
  // - They had already been loaded
  // - Sections had been opened
  // Otherwise, all checklist data will remain in the cache and users will interact with checklist without loading anything from the network
  // until a new product has been added to the registry. When that happens we need to remove all this cache items since we don't have enough
  // context to modify the cache accordingly

  // Remove Progress Sections by ID from the cache
  const registryChecklistQueryBaseProps = {
    query: GetRegistryChecklistDocument,
    variables: { eventId },
  };

  const registryChecklistQueryCacheItem = cache.readQuery<GetRegistryChecklistQuery>({
    ...registryChecklistQueryBaseProps,
  });

  if (registryChecklistQueryCacheItem?.getRegistryChecklist?.checklistSections) {
    registryChecklistQueryCacheItem.getRegistryChecklist.checklistSections.forEach(({ id }) => {
      const registryChecklistSectionQueryBaseProps = {
        query: GetRegistryChecklistSectionByIdDocument,
        variables: { id },
      };

      const registryChecklistSectionQueryCacheItem = cache.readQuery<GetRegistryChecklistSectionByIdQuery>({
        ...registryChecklistSectionQueryBaseProps,
      });

      if (registryChecklistSectionQueryCacheItem?.getRegistryChecklistSectionById) {
        cache.evict({ id: cache.identify(registryChecklistSectionQueryCacheItem.getRegistryChecklistSectionById) });
      }
    });
  }

  // Remove Checklist Progress from the cache
  const registryChecklistProgressQueryBaseProps = {
    query: GetRegistryChecklistDocument,
    variables: { eventId },
  };

  const registryChecklistProgressQueryCacheItem = cache.readQuery<GetRegistryChecklistQuery>({
    ...registryChecklistProgressQueryBaseProps,
  });

  if (registryChecklistProgressQueryCacheItem?.getRegistryChecklist) {
    cache.evict({ id: cache.identify(registryChecklistProgressQueryCacheItem.getRegistryChecklist) });
  }

  // Garbage collection after all evictions
  cache.gc();
};
